import { createContext, useContext, useState, useEffect } from "react";
import {
  Calculator, Globe2, FileSpreadsheet, ScrollText, Landmark, PiggyBank,
  Receipt, TrendingUp, Wallet, ClipboardCheck, Building2, Boxes,
} from "lucide-react";

const AICONS = [PiggyBank, ScrollText, FileSpreadsheet, ClipboardCheck, Receipt, Landmark, TrendingUp, Calculator, Wallet, Boxes, Globe2, Building2];
const svc = (arr) => arr.map((x, i) => ({ icon: AICONS[i], title: x[0], desc: x[1] }));

export const CONTENT = {
  en: {
    nav: {
      links: [
        { label: "Services", href: "#services" },
        { label: "Boutique", href: "#boutique" },
        { label: "Contact", href: "#contact" },
      ],
      fullMenu: [
        { label: "Services", href: "#services" },
        { label: "Boutique", href: "#boutique" },
        { label: "Contact", href: "#contact" },
        { label: "Track Record", href: "#record" },
        { label: "About Us", href: "#about" },
      ],
      cta: "Book a Free Consultation",
    },
    hero: {
      overline: "Bookkeeping · Accounting · Marketing · Recruiting",
      h1a: ["Understand", "your numbers,"],
      h1b: "improve your profitability.",
      lead: "Bookkeeping & accounting services for small to mid-size companies. We don't only deliver the numbers, we tell you what they mean.",
      cta: "Request a Quote",
      stats: [
        { value: "20+", label: "Years in accounting" },
        { value: "30+", label: "Companies supported" },
      ],
    },
    marquee: ["International Transactions", "Consolidation", "Month & Year Ends", "Audit Support", "Sales Tax", "Corporate Income Tax", "Budgeting & Forecasting", "Cost Analysis", "Bookkeeping", "Payroll", "AP / AR", "Reconciliation", "Tax Credits & Grants"],
    about: {
      overline: "About Us",
      headingA: "Numbers, strategy ", headingAccent: "& people", headingB: " — under one roof.",
      p1: "Ancion Consulting is a boutique practice led by Sony Ancion, built on over 20 years of experience, including advanced and international accounting, across over 15 different industries in Canada, USA, and Europe. We serve small, medium and large companies that want the rigor of a seasoned finance team without the overhead of one.",
      p2: "Accounting is our core — bookkeeping, consolidations, month & year-end closes, sales tax, corporate income tax, payroll, budgeting, forecasting and audit support. Alongside it we offer marketing strategy and recruiting, so growing businesses have one dependable, bilingual (English & French) partner across the numbers, the growth and the people.",
      facts: [
        { value: "20+", label: "Years in accounting" },
        { value: "CA · US · EU", label: "Cross-border reach" },
        { value: "EN · FR", label: "Bilingual service" },
        { value: "3", label: "Accounting · Marketing · Recruiting" },
      ],
      founderOverline: "Meet the founder",
      founderName: ["Sony", "Ancion."],
      caption: "Founder & Lead Accountant · MBA (AI) candidate · B.Sc. Accounting, UQÀM · College Degree in Marketing, Collège de Rosemont",
      chapters: [
        { n: "01", title: "The Expertise", body: "Two decades inside the numbers — from the National Bank of Canada to senior roles at international firms. We handle intercompany transactions, complex consolidations, month & year-ends, audits and tax with a prudent, methodical hand." },
        { n: "02", title: "The Reach", body: "Multi-jurisdiction by nature. We've owned full accounting cycles for companies in Canada, the United States and Europe — sales tax, payroll and financial reporting across borders and currencies." },
        { n: "03", title: "The Approach", body: "Highly organized, deadline-driven and protective of the integrity of your financial data. Great communication, sharp critical thinking, and a genuine partner mindset for small & medium businesses." },
      ],
    },
    services: {
      overline: "01 — The Core Practice",
      headingA: "Bookkeeping & Accounting", headingAccent: "service.",
      introList: ["Bookkeeping Catch Up & Cleaning", "Monthly & Quarterly Financial Statements Production & Analysis", "Billing, Accounts Receivable & Accounts Payable", "Sales Tax & Income Tax Reporting", "Federal Grant & Subvention Applications"],
      beyond: "Beyond the books",
      enquire: "Enquire →",
      items: svc([
        ["Bookkeeping", "Accurate day-to-day books for companies across a range of industries."],
        ["Accounts Payable / Receivable", "High-volume AP/AR, billing, collections and supplier reconciliation."],
        ["Month-End & Year-End Close", "Closing journal entries, reviews and full cycle close managed to deadline."],
        ["Audit Preparation & Support", "Internal audits and complete tax & audit document packages for external firms."],
        ["Sales Tax Filing", "GST/QST and US/Swiss sales tax reporting for all entities, filed on time."],
        ["Corporate Income Tax", "Corporate tax coordination plus Federal & Provincial R&D tax credit preparation."],
        ["Budgeting & Forecasting", "Sales projections, budgets and financial performance analysis for the road ahead."],
        ["Cost & Financial Analysis", "Ratio analysis, cost breakdowns and variance investigation of results vs. forecast."],
        ["Payroll", "Full payroll administration and compliance across multiple jurisdictions."],
        ["Inventory & Grants", "Multi-location inventory management and Federal grant & subvention tracking."],
        ["International Accounting", "Intercompany transactions and multi-entity books across Canada, the US & Europe."],
        ["Consolidations", "Monthly & quarterly consolidation of subsidiaries into clean, audit-ready statements."],
      ]),
      others: [
        { tag: "02", title: "Marketing Strategy", desc: "Prospection strategies and marketing projects designed to lift periodic sales — informed by a formal specialization in Marketing and hands-on advisory for growing firms.", points: ["Digital Marketing", "Prospection & sales growth strategy", "Go-to-market advisory", "Performance-driven positioning"] },
        { tag: "03", title: "Recruiting Services", desc: "End-to-end hiring support: sourcing, interviewing candidates and HR coordination so your team scales with the right people at the right time.", points: ["Candidate sourcing & screening", "Structured interviewing", "HR process & onboarding"] },
      ],
    },
    record: {
      overline: "Track Record",
      heading: "Two decades of numbers you can trust.",
      items: [
        { period: "2023 — 2026", role: "Senior Accountant", org: "Syneos Health LP, Toronto, Ont", note: "R&D tax credits, reconciliations, inventory across QC, Princeton & Miami." },
        { period: "2022 — Now", role: "Accounting Consultant", org: "Independent, Montréal, Qc", note: "Bookkeeping, financials, budgeting & forecasting for CA & US clients." },
        { period: "2020 — 2022", role: "Accountant", org: "Thevoz Attorneys, Austin TX", note: "Full accounting cycles & payroll for Swiss and US firms." },
        { period: "2016 — 2019", role: "Staff Accountant", org: "Kent Security, Miami FL", note: "Internal audits & consolidation across five state subsidiaries." },
        { period: "2014 — 2016", role: "Accountant", org: "Atome Distributeur Inc., Montréal, Qc", note: "Managed cash flow, AP/AR & billing, daily entries, bank reconciliations, tax reports and budgets." },
        { period: "2014", role: "Accounting Specialist", org: "TUI Leisure Travel / Ascendo (Consultant), Miami FL", note: "Cost analysis of cruise ships and processed high volumes of AP & AR." },
        { period: "2013", role: "Accounting Teacher", org: "Collège Essor, Montréal, Qc", note: "Built a course from scratch and taught the basics of accounting to students enrolled in an intensive program that leads to an internship on the job market." },
        { period: "2005 — 2012", role: "Financial Analyst", org: "National Bank of Canada, Montréal, Qc", note: "Corporate loan analysis, valuations & financial consolidation." },
      ],
    },
    boutique: {
      overline: "The Boutique",
      headingA: "Ebooks, courses", headingAccent: "& invoices.",
      lead: "Buy our digital products with secure Stripe checkout — or settle an existing invoice in seconds.",
      kind: { Ebook: "Ebook", Course: "Course" },
      buy: "Buy now", redirecting: "Redirecting…", readBack: "Read back cover",
      invoice: { tag: "Pay an invoice", title: "Settle a balance", desc: "Have an invoice from us? Enter its number and amount to pay securely.", number: "Invoice number", amount: "Amount (USD)", pay: "Pay invoice" },
    },
    contact: {
      overline: "Let's talk",
      heading: "Request a quote or ask a question.",
      lead: "Tell us about your business and what you need. We'll get back to you personally to arrange a consultation.",
      purposes: ["Request a quote", "Ask a question"],
      services: ["Accounting Services", "Marketing Strategy", "Recruiting Services", "General Enquiry"],
      ph: { name: "Full name *", company: "Company", email: "Email address *", message: "How can we help? *" },
      submitQuote: "Request my quote", submitQuestion: "Send my question", sending: "Sending…",
      toastFill: "Please fill in your name, email and message.",
      toastThanks: "Thank you — we'll be in touch shortly.",
      toastErr: "Something went wrong. Please try again.",
    },
    footer: {
      tagline: "Precision accounting, marketing strategy and recruiting for ambitious small to medium companies.",
      navigate: "Navigate", contact: "Contact", geo: "Canada · USA · Europe",
    },
  },

  fr: {
    nav: {
      links: [
        { label: "Services", href: "#services" },
        { label: "Boutique", href: "#boutique" },
        { label: "Contact", href: "#contact" },
      ],
      fullMenu: [
        { label: "Services", href: "#services" },
        { label: "Boutique", href: "#boutique" },
        { label: "Contact", href: "#contact" },
        { label: "Parcours", href: "#record" },
        { label: "À propos", href: "#about" },
      ],
      cta: "Consultation gratuite",
    },
    hero: {
      overline: "Tenue de livres · Comptabilité · Marketing · Recrutement",
      h1a: ["Comprenez", "vos chiffres,"],
      h1b: "améliorez votre rentabilité.",
      lead: "Services de tenue de livres et de comptabilité pour les petites et moyennes entreprises. Nous ne livrons pas seulement les chiffres, nous vous expliquons ce qu'ils signifient.",
      cta: "Demander une soumission",
      stats: [
        { value: "20+", label: "Années en comptabilité" },
        { value: "30+", label: "Entreprises accompagnées" },
      ],
    },
    marquee: ["Transactions internationales", "Consolidation", "Fins de mois et d'année", "Soutien à l'audit", "Taxes de vente", "Impôt des sociétés", "Budgets et prévisions", "Analyse des coûts", "Tenue de livres", "Paie", "CF / CR", "Rapprochements", "Crédits et subventions"],
    about: {
      overline: "À propos",
      headingA: "Les chiffres, la stratégie ", headingAccent: "et les gens", headingB: " — sous un même toit.",
      p1: "Ancion Consulting est un cabinet boutique dirigé par Sony Ancion, fort de plus de 20 ans d'expérience, incluant la comptabilité avancée et internationale, dans plus de 15 secteurs au Canada, aux États-Unis et en Europe. Nous servons les petites, moyennes et grandes entreprises qui recherchent la rigueur d'une équipe financière chevronnée, sans en assumer les coûts.",
      p2: "La comptabilité est notre cœur de métier — tenue de livres, consolidations, clôtures de fin de mois et d'année, taxes de vente, impôt des sociétés, paie, budgets, prévisions et soutien à l'audit. S'y ajoutent la stratégie marketing et le recrutement : un partenaire unique, fiable et bilingue (français et anglais), des chiffres à la croissance jusqu'aux talents.",
      facts: [
        { value: "20+", label: "Années en comptabilité" },
        { value: "CA · US · EU", label: "Portée transfrontalière" },
        { value: "EN · FR", label: "Service bilingue" },
        { value: "3", label: "Comptabilité · Marketing · Recrutement" },
      ],
      founderOverline: "Le fondateur",
      founderName: ["Sony", "Ancion."],
      caption: "Fondateur et comptable en chef · Candidat au MBA (IA) · B. Sc. comptabilité, UQÀM · DEC en marketing, Collège de Rosemont",
      chapters: [
        { n: "01", title: "L'expertise", body: "Deux décennies au cœur des chiffres — de la Banque Nationale du Canada à des postes seniors dans des firmes internationales. Nous gérons les transactions intersociétés, les consolidations complexes, les fins de mois et d'année, les audits et la fiscalité avec rigueur et méthode." },
        { n: "02", title: "La portée", body: "Multi-juridictionnel par nature. Nous avons pris en charge des cycles comptables complets pour des entreprises au Canada, aux États-Unis et en Europe — taxes de vente, paie et rapports financiers, à travers les frontières et les devises." },
        { n: "03", title: "L'approche", body: "Organisés, orientés échéances et soucieux de l'intégrité de vos données financières. Une excellente communication, un esprit d'analyse aiguisé et une véritable mentalité de partenaire pour les PME." },
      ],
    },
    services: {
      overline: "01 — Le cœur de métier",
      headingA: "Tenue de livres &", headingAccent: "comptabilité.",
      introList: ["Rattrapage et nettoyage de la tenue de livres", "Production et analyse d'états financiers mensuels et trimestriels", "Facturation, comptes clients et comptes fournisseurs", "Déclarations de taxes de vente et d'impôt", "Demandes de subventions fédérales"],
      beyond: "Au-delà des chiffres",
      enquire: "Nous joindre →",
      items: svc([
        ["Tenue de livres", "Une comptabilité quotidienne précise pour des entreprises de divers secteurs."],
        ["Comptes fournisseurs / clients", "CF/CR à fort volume, facturation, recouvrement et rapprochement des fournisseurs."],
        ["Clôtures de fin de mois et d'année", "Écritures de clôture, révisions et cycle complet gérés dans les délais."],
        ["Préparation et soutien à l'audit", "Audits internes et dossiers fiscaux et d'audit complets pour les firmes externes."],
        ["Déclarations de taxes de vente", "TPS/TVQ et taxes de vente US/suisses pour toutes les entités, produites à temps."],
        ["Impôt des sociétés", "Coordination de l'impôt des sociétés et préparation des crédits R&D fédéraux et provinciaux."],
        ["Budgets et prévisions", "Projections de ventes, budgets et analyse de la performance financière à venir."],
        ["Analyse des coûts et financière", "Analyse de ratios, ventilation des coûts et écarts entre résultats et prévisions."],
        ["Paie", "Administration complète de la paie et conformité dans plusieurs juridictions."],
        ["Inventaire et subventions", "Gestion d'inventaire multi-sites et suivi des subventions fédérales."],
        ["Comptabilité internationale", "Transactions intersociétés et livres multi-entités au Canada, aux É.-U. et en Europe."],
        ["Consolidations", "Consolidation mensuelle et trimestrielle des filiales en états prêts pour l'audit."],
      ]),
      others: [
        { tag: "02", title: "Stratégie marketing", desc: "Des stratégies de prospection et des projets marketing conçus pour augmenter les ventes périodiques — appuyés par une spécialisation formelle en marketing et un accompagnement concret des entreprises en croissance.", points: ["Marketing numérique", "Stratégie de prospection et de croissance des ventes", "Conseil de mise en marché", "Positionnement axé sur la performance"] },
        { tag: "03", title: "Services de recrutement", desc: "Un soutien au recrutement de bout en bout : sourcing, entrevues des candidats et coordination RH pour faire grandir votre équipe avec les bonnes personnes au bon moment.", points: ["Sourcing et présélection des candidats", "Entrevues structurées", "Processus RH et intégration"] },
      ],
    },
    record: {
      overline: "Parcours",
      heading: "Deux décennies de chiffres dignes de confiance.",
      items: [
        { period: "2023 — 2026", role: "Comptable senior", org: "Syneos Health LP, Toronto, Ont", note: "Crédits d'impôt R&D, rapprochements et inventaire au QC, à Princeton et Miami." },
        { period: "2022 — Aujourd'hui", role: "Consultant comptable", org: "À son compte, Montréal, Qc", note: "Tenue de livres, états financiers, budgets et prévisions pour des clients au Canada et aux É.-U." },
        { period: "2020 — 2022", role: "Comptable", org: "Thevoz Attorneys, Austin TX", note: "Cycles comptables complets et paie pour des firmes suisses et américaines." },
        { period: "2016 — 2019", role: "Comptable", org: "Kent Security, Miami FL", note: "Audits internes et consolidation de cinq filiales dans plusieurs États." },
        { period: "2014 — 2016", role: "Comptable", org: "Atome Distributeur Inc., Montréal, Qc", note: "Gestion de trésorerie, CF/CR et facturation, écritures quotidiennes, rapprochements bancaires, rapports fiscaux et budgets." },
        { period: "2014", role: "Spécialiste comptable", org: "TUI Leisure Travel / Ascendo (Consultant), Miami FL", note: "Analyse des coûts de navires de croisière et traitement de gros volumes de CF et CR." },
        { period: "2013", role: "Enseignant en comptabilité", org: "Collège Essor, Montréal, Qc", note: "Conception d'un cours à partir de zéro et enseignement des bases de la comptabilité à des étudiants d'un programme intensif menant à un stage en entreprise." },
        { period: "2005 — 2012", role: "Analyste financier", org: "Banque Nationale du Canada, Montréal, Qc", note: "Analyse de prêts aux entreprises, évaluations et consolidation financière." },
      ],
    },
    boutique: {
      overline: "La boutique",
      headingA: "Livres, cours", headingAccent: "et factures.",
      lead: "Achetez nos produits numériques via un paiement sécurisé Stripe — ou réglez une facture en quelques secondes.",
      kind: { Ebook: "Livre numérique", Course: "Cours" },
      buy: "Acheter", redirecting: "Redirection…", readBack: "Lire la 4e de couverture",
      invoice: { tag: "Payer une facture", title: "Régler un solde", desc: "Vous avez une facture de notre part ? Entrez son numéro et son montant pour payer en toute sécurité.", number: "Numéro de facture", amount: "Montant (USD)", pay: "Payer la facture" },
    },
    contact: {
      overline: "Parlons-en",
      heading: "Demandez une soumission ou posez une question.",
      lead: "Parlez-nous de votre entreprise et de vos besoins. Nous vous répondrons personnellement pour organiser une consultation.",
      purposes: ["Demander une soumission", "Poser une question"],
      services: ["Services de comptabilité", "Stratégie marketing", "Services de recrutement", "Demande générale"],
      ph: { name: "Nom complet *", company: "Entreprise", email: "Adresse courriel *", message: "Comment pouvons-nous aider ? *" },
      submitQuote: "Demander ma soumission", submitQuestion: "Envoyer ma question", sending: "Envoi…",
      toastFill: "Veuillez indiquer votre nom, votre courriel et votre message.",
      toastThanks: "Merci — nous vous recontacterons sous peu.",
      toastErr: "Une erreur est survenue. Veuillez réessayer.",
    },
    footer: {
      tagline: "Comptabilité de précision, stratégie marketing et recrutement pour les petites et moyennes entreprises ambitieuses.",
      navigate: "Navigation", contact: "Contact", geo: "Canada · USA · Europe",
    },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const toggle = () => setLang((l) => (l === "en" ? "fr" : "en"));
  return (
    <LangContext.Provider value={{ lang, setLang, toggle, c: CONTENT[lang] }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
